import { TCustomFont } from '@honeybook/hbui/dist/styles/fonts';
import { TSize } from '@honeybook/hbui/dist/tokens/types';
import colors from '@honeybook/hbui/dist/styles/colors';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import { RelatedFlowModel } from '~/models/related-flow.model';
import {
    Currency,
    IBillFromContent,
    ICompanyResponse,
    IInvoiceProjectDetails,
    ILastContentEdit,
    IMember,
    IMinimalPaymentVariableValue,
    IOnboardingContainer,
    IPendingChange,
    IPublisher,
    ITaxResponse,
    IUserResponse,
    IWorkspaceUserDataResponse,
    TEmailType,
    TFlowType,
    TServiceSelectionBlockType,
    TSessionTimeSlot,
    IWorkspaceResponse
} from '~/types/server.types';
import { Company } from '~/models/company.model';
import { Page } from '~/models/page.model';
import { Variable } from '~/models/variable.model';
import { IBlockFakeCkEditorStyles } from '~/types/block-styles.types';
import { Project } from '~/models/project.model';
import { TPlanCode } from '~/components/Membership/types/membership.types';
import {
    TContainerableBlock,
    TResponseBlock,
    TResponsesBlockTypes
} from '~/types/builder.types';
import { QuestionBlock } from '~/models/blocks/question-block.model';
import type { ChargeContainerBlock as ChargeContainerBlockModel } from '~/models/blocks/charge-container-block.model';
import type { ContractBlock as ContractBlockModel } from '~/models/blocks/contract-block.model';
import type { ServiceSelectionBlock as ServiceSelectionBlockModel } from '~/models/blocks/service-selection-block.model';
import type { InvoiceBlock as InvoiceBlockModel } from '~/models/blocks/invoice-block.model';
import type { ScheduleContainerBlock as ScheduleContainerBlockModel } from '~/models/blocks/schedule-container-block.model';
import type { TShortTimezone } from '~/utils/timezone.utils';
import {
    TBlockComponentsFilterByBlockTypes,
    TFlowActionType
} from '~/components/CreateFlow/types/create-flow.types';
import { TFeatureVariation } from '~/components/FeatureRestriction/types/feature-restriction.types';
import { TSort } from '@honeybook/hbui/dist/components/HBNewTable/HBNewTable.types';
import { TWorkspaceMemberType } from '~/components/Contacts/contacts.types';
import { IContactEnrichment } from '~/components/Contacts/contact-enrichment.types';
import { Flow } from '~/models/flow.model';
import { TSortFunctionKeys } from '~/apps/main/ServiceTemplates/useServiceTemplates';
import { HbUserType, UserType } from '~/types/enums.types';
import { IOrganization } from '~/components/OrganizationView/types/organization-view.types';
import { z } from 'zod';
import { IPicTimeConnectedGallery } from '~/apps/main/PicTime/pic-time.types';
import { THBExecutionStatus } from '~/packages/automations-core';
import { ExecutionStatus } from 'n8n-workflow';
import { SmartFieldPermissions } from '~/components/Builder/BuilderSidebar/ContractSidebar/SmartFieldSidebar/SmartField.types';
import { ICustomFieldValue } from '~/components/CustomFields/custom-field-schemas.types';
import { Option } from '~/components/CompanySettings/Integrations/QuickbooksDashboard';
import { TOneComposerSmartFields } from '~/common/SmartFields/smart-fields.types';
import { TPartner } from '~/apps/main/Partners/partners.types';
import { IButtonTheme } from '~/common/Themes/brand-theme.types';
import { IClientPortalThemeResponse } from '~/components/ClientExperience/ClientPortal/client-portal.types';
import type {
    TTemplateType,
    TTemplateViewModelKey
} from '~/components/Templates/types/templates.types';
import { TUserTags } from '~/apps/main/OnboardingQuiz/OnboardingQuiz.types';

export interface IFlowBodyTextThemeStyles {
    font_family?: TCustomFont;
    background_color?: string;
    color?: string;
    accent_color?: string;

    // Keys for updating the theme using the personalizeTemplate API
    text_color?: string;
    button_background_color?: string;
}

export interface IFlowHeadingTextThemeStyles {
    font_family?: TCustomFont;
    color?: string;
}

export interface IFlowThemeButtonStyles extends IButtonTheme {
    accent_color?: string;
}

export interface IFlowThemeQuestionStyles {
    font_family?: TCustomFont;
    size?: TSize;
    color?: string;
    warning_color?: string;
    background_color?: string;
    border_color?: string;
    border_radius?: number;
}

export interface IFlowTheme {
    styles: IFlowBodyTextThemeStyles; //body_styles
    buttons_styles: IFlowThemeButtonStyles;
    questions_styles: IFlowThemeQuestionStyles;
    heading_styles?: IFlowHeadingTextThemeStyles;
}

export interface IClientPortalTheme {
    title: string;
    background_color: string;
    has_logo: boolean;
    has_icon: boolean;
    logo: {
        show: boolean;
        type: string;
        logo_url: string;
        icon_url: string;
    };
    brand: {
        brand_color: string;
        text_color: string;
    };
    is_powered_by_hb?: boolean;
    icon?: {
        url: string;
        position_x: number;
        position_y: number;
        position_w: number;
        position_h: number;
    };
    client_portal_branding?: IClientPortalThemeResponse;
}

export interface IClientPortalConfiguration extends IClientPortalTheme {
    placement_id: string;
    title: string;
    last_published_date: string;
    company_id: string;
    client_portal_config_published_schema_version: number;
    account_id: string;
    company_data_for_flows: Record<string, any>;
}

export type TSummaryIcon = 'cart' | 'list';

export type MobileServiceItemUnitOption = {
    value: ServiceItemUnit;
    label: string;
};

export interface IFlowSummary {
    show_total_amount: boolean;
    summary_icon: TSummaryIcon;
}

export type TFlowThemeMainKey = keyof IFlowTheme;
export type TFlowThemeKey =
    | keyof IFlowBodyTextThemeStyles
    | keyof IFlowThemeButtonStyles
    | keyof IFlowThemeQuestionStyles;

export type TFlowTag =
    | 'contract_block'
    | 'invoice_block'
    | 'payment_schedule_block'
    | 'service_selection_block'
    | 'container_block'
    | 'company_info_block'
    | 'text_block';

export type TAssignTeamMemberRole =
    | 'submission_recipient'
    | 'workspace_member'
    | 'project_owner';

export interface IVariable {
    connected_block_id: string;
    raw_value?: string;
    value: any;
    variable_id: string;
    invalidated_on: string;
    diverged_on: string;
}

export type TExpirationTimes = 'hours' | 'days' | 'weeks' | 'months';
export type TExpirationCondition = 'after_sent' | 'is_viewed';

export enum ExpirationState {
    Active = 'active',
    Stopped = 'stopped'
}

export interface IAutoExpiration {
    ends_on?: string;
    expired?: boolean;
    is_forced_expired?: boolean;
    message?: string;
    reactivate?: boolean;
    show_in_flow?: boolean;
    start_expire_condition?: TExpirationCondition;
    started_on?: string;
    state?: ExpirationState;
    unit_count?: number;
    unit_type?: TExpirationTimes;
}

export interface IFlowConfigurationResponse {
    default_email_template_id?: string;
    is_redirect_url_on?: boolean;
    create_new_project?: boolean;
    redirect_url?: string;
    auto_expiration?: IAutoExpiration;
    require_authentication?: boolean;
    create_draft_from_services_selection?: boolean;
    flow_template_for_draft?: string;
    cta_button_text?: string;
    is_logo_enabled?: boolean;
    is_workflow_activated?: boolean;
    is_email_confirmation_activated?: boolean;
    viewer_link_sharing_enabled?: boolean;
    view_only_flow_url?: string;
    assigned_members?: IFlowConfigurationMembers;
    flow_link_slug?: string;
    flow_link_slug_reached_limit?: boolean;
    public_configuration?: IFlowPublicConfiguration;
    project_type?: string;
}

export interface IFlowConfigurationMembers {
    project_owner?: string;
    submission_recipients?: string[];
    workspace_participants?: string[];
}

export interface IReferralDetails {
    code?: string;
    discount_amount?: string;
    discount_duration?: string;
}

export interface IFlowPublicConfiguration {
    public_url_cx: string;
    published_template?: boolean;
    offer_referral?: boolean;
    referral_details?: IReferralDetails;
    public_url: string;
    show_international_modal?: boolean;
    is_gallery_template?: boolean;
    selled_by_lead_form_id?: string;
    selled_lead_form_url?: string;
}

export type TFlowPermDisableEditReasons =
    | 'file_owner'
    | 'owner'
    | 'super_admin'
    | 'admin'
    | 'moderator'
    | 'project_owner';

export type TFlowPermissionType = 'full_access' | 'no_permissions';
export interface IFlowPermission {
    user_id: string;
    permission: TFlowPermissionType;
    disabled?: TFlowPermDisableEditReasons;
}

export interface IFlowPermissionWActions extends IFlowPermission {
    can_edit: boolean;
}

export interface IFlowConfigurationMember {
    _id?: string;
    initials?: string;
    full_name?: string;
    email?: string;
    profile_image_url?: string;
    email_signatures?: IEmailSignature[];
}

export interface IFlowConfigurationMemberWithRole
    extends IFlowConfigurationMember {
    role?: string;
}

// TODO: remove this in favour of using IFlowResponse + Flow model
export interface IFlow {
    _id: string;
    canceled?: ICanceled;
    card_on_file_enabled?: boolean;
    changes_pending_approval?: IPendingChange[];
    action_block_types?: TActionBlocks[];
    company_id?: string;
    owner_id?: string;
    company_team_members?: IFlowConfigurationMember[];
    configuration?: FlowConfigurationModel;
    created_at?: string;
    currency?: Currency;
    deactivated_public_flow?: boolean;
    expired?: boolean;
    first_published_at?: string;
    first_viewed_at?: string;
    flow_type?: TFlowType;
    flow_url?: string;
    folder_id?: string;
    fullstory_force_record_cx?: boolean;
    gratuity_variation?: TGratuityVariation;
    has_invoice?: boolean;
    is_eligible_for_mark_as_completed?: boolean;
    isDirty?: boolean;
    is_booked?: boolean;
    is_deletable?: boolean;
    is_published?: boolean;
    is_template?: boolean;
    last_action_number?: number;
    last_changed_at?: string;
    last_content_edit?: ILastContentEdit;
    last_viewed_at?: string | null;
    members?: IMember[];
    original_theme?: IFlowTheme;
    pageIds?: string[];
    path?: string;
    project_id?: string;
    published_at?: string;
    published_by_user?: { full_name: string };
    related_flows?: RelatedFlowModel[];
    revision_count?: number;
    revision_id?: string;
    share_flow_pending_task_data?: IVendorEmail;
    starred?: boolean;
    status?: TFlowStatus;
    submitted_by_user?: object;
    submitted_on?: string;
    summary?: IFlowSummary;
    tags?: TFlowTag[];
    theme: IFlowTheme;
    thumbnail_image?: IFlowThumbnail;
    title?: string;
    variables: IVariable[];
    version_number?: number;
    view_only_flow_url?: string;
    was_already_published?: boolean;
    workspace_id?: string;
    publisher?: IPublisher;
    public_configuration?: IFlowPublicConfiguration;
    smart_fields?: TOneComposerSmartFields;
    flow_permissions?: IFlowPermission[];
    require_strong_auth?: boolean;
}

export type TNonContainerBlockType =
    | 'text_block'
    | 'social_block'
    | 'image_block'
    | 'question_block'
    | 'video_block'
    | 'spacer_block'
    | 'contract_html_block'
    | 'payment_block'
    | 'invoice_header_block'
    | 'signature_block'
    | 'charge_block'
    | 'invoice_column_header_block'
    | 'invoice_summary_block'
    | 'iframe_block'
    | 'schedule_session_block';

export type TContainerableBlockType =
    | TRootBlock
    | 'invoice_service_item_block'
    | 'payment_schedule_block'
    | 'recurring_schedule_block'
    | 'service_items_container_block'
    | 'service_selection_item_block'
    | 'services_block'
    | 'signatures_block';

export const ContainerableBlockType: Record<TContainerableBlockType, true> = {
    contract_block: true,
    payment_schedule_block: true,
    service_selection_block: true,
    container_block: true,
    company_info_block: true,
    charge_container_block: true,
    invoice_container_block: true,
    lead_details_block: true,
    schedule_container_block: true,
    invoice_service_item_block: true,
    service_selection_item_block: true,
    signatures_block: true,
    services_block: true,
    service_items_container_block: true,
    recurring_schedule_block: true
};

export type TBlockType = TContainerableBlockType | TNonContainerBlockType;

// use this if you want to Extract a subset of TBlockType:
export type TSubsetBlockType<T extends string, U extends T> = Extract<T, U>;

export type TRootBlock =
    | 'charge_container_block'
    | 'company_info_block'
    | 'container_block'
    | 'contract_block'
    | 'invoice_container_block'
    | 'lead_details_block'
    | 'schedule_container_block'
    | 'service_selection_block';

export type TActionBlocks = Extract<
    TContainerableBlockType,
    | 'contract_block'
    | 'invoice_container_block'
    | 'payment_schedule_block'
    | 'recurring_schedule_block'
    | 'charge_container_block'
    | 'services_block'
    | 'service_selection_block'
    | 'schedule_container_block'
>;

export type TCreatableActionBlocks = Exclude<
    TActionBlocks,
    'payment_schedule_block' | 'recurring_schedule_block' | 'services_block'
>;

export type SelectableBlockType = Exclude<
    TBlockType,
    | 'invoice_column_header_block'
    | 'invoice_header_block'
    | 'invoice_summary_block'
    | 'charge_block'
    | 'schedule_session_block'
    | 'signatures_block'
    | 'services_block'
    | 'payment_block'
    | 'payment_schedule_block'
    | 'recurring_schedule_block'
>;

export type TPageActionBlocks = Extract<
    TContainerableBlock,
    | ChargeContainerBlockModel
    | ContractBlockModel
    | InvoiceBlockModel
    | ServiceSelectionBlockModel
    | ScheduleContainerBlockModel
>;

export type TBlockSubType = CompositionType;

export enum CompositionType {
    Default = 'default',
    CompanyInfo = 'company_info'
}

export type TLayoutType = 'vertical' | 'horizontal';

export interface IEntity {
    _id: string;
}

export interface IChildLayout {
    block_id: string;
    size: number;
    order: number;
    initialAttributes?: any;
}

export type IEmailSignature = {
    _id?: string;
    created_at?: Date | string;
    title?: string;
    html_body: string;
};

export type SystemUserType = 'vendor' | 'client';

export enum UserStatus {
    Activated = 'ACTIVATED',
    Trial = 'TRIAL',
    PreTrailer = 'PRE-TRIALER',
    TrialExpired = 'EXPIRED_TRIAL',
    Subscribed = 'SUBSCRIBED',
    Honeypaid = 'HONEYPAID',
    SubCancel = 'SUB_CANCEL',
    ActivatedSubChurn = 'ACTIVATED_SUB_CHURN'
}

export enum PricingPlan {
    Lifetime = 'lifetime',
    Monthly = 'monthly',
    Annual = 'annual',
    Free = 'free',
    Manual = 'manual',
    MonthlyBasic = 'monthly_basic',
    StarterMonthly = 'starter_monthly',
    UnlimitedAnnual = 'unlimited_annual',
    UnlimitedMonthly = 'unlimited_monthly'
}

export type IFileTemplate = ITemplateBase & {
    file_title?: string;
    last_update_time?: string;
    sent_on?: string;
    file_type?: string;
    project_name?: string;
};

export type IFlowTemplateServices = {
    layout: string;
    selection_type: string;
    selection_required: boolean;
    count: number;
};

export type IFlowTemplate = ITemplateBase & {
    flow_type?: TFlowType;
    is_published?: boolean;
    has_pending_changes?: boolean;
    scheduled_to_be_sent?: string;
    has_contract?: boolean;
    has_invoice?: boolean;
    require_authentication?: boolean;
    tags?: TFlowTag[];
    action_block_types?: TActionBlocks[];
    pages_count?: number;
    questions_count?: number;
    starred?: boolean;
    folder_id?: string;
    path?: string;
    services?: IFlowTemplateServices;
    thumbnail_image?: IFlowThumbnail;
    sent_on?: string;
    flow_url?: string;
    public_flow_status?: LeadFormStatus;
    public_configuration?: IFlowPublicConfiguration;
    source_flow_id?: string;
    is_selling_template?: boolean;
};

export type IRecentlySent = {
    files: IFileTemplate[];
    flows: IFlowTemplate[];
};

export interface FlowTemplateActionBlockData {
    count?: number;
    singular_block_id?: string;
    selection_type?: ServiceSelectionType;
    service_selection_type?: TServiceSelectionBlockType;
    service_items_count?: number;
    total_price?: number;
    invoice_type?: TInvoiceType;
    first_payment_content?: IMinimalPaymentContent;
    first_payment_variable?: IMinimalPaymentVariableValue;
    repetition_unit_type?: TRepetitionUnitType;
    custom_unit_type?: TCustomTimeUnitType;
    rest_payment_count?: number;
    per_block_data?: Array<{
        _id?: string;
        first_line?: string;
        page?: {
            title?: string;
            number?: number;
        };
    }>;
    tags?: string[];
}

export interface IEnrichedFlowTemplate extends IFlowTemplate {
    is_block_template: boolean;
    action_block_data: FlowTemplateActionBlockData;
}

export interface ISendTime {
    day: string;
    hour: string;
}

export interface IEmailAttachmentProps {
    file_attachments?: IEmailAttachment[];
    image_attachments?: IEmailAttachment[];
}

export interface IVendorEmail extends IEmailAttachmentProps, ITemplateBase {
    bcc?: string;
    cc?: string;
    dataOnly?(): IVendorEmail;
    email_type?: TEmailType;
    encoded?: boolean;
    from?: string;
    html_body: string;
    sent_on?: string;
    subject: string;
    time_to_send?: Partial<ISendTime>;
    title?: string;
    to?: string;
}

export interface IFeedItemTarget {
    full_name?: string;
    profile_img_url?: string;
    gallery: IPicTimeConnectedGallery;
}

export interface IFeedItemData {
    html_body: string;
    plain_text: string;
    sender_user_id: string;
    sent_on: string;
    action_type?: string;
    subject: string;
    system_generated?: boolean;
    preferences_automations_generated?: boolean;
    email_type_cd?: number;
    files?: IEmailAttachment[];
    images?: IImage[];
    workspace_files?: ILegacyFilePreview[];
    _id?: string;
    pvl_id?: string;
    sent_to?: string;
    session_date_time_end?: string;
    session_title?: string;
    session_date_time_start?: string;
    on_object_id: string;
    target?: IFeedItemTarget;
}

export interface IEnrichedFeedItemData extends IContactEnrichment {
    contact_id: string;
}

export interface IContactEnrichmentFeedItem {
    _id: string;
    created_at: string;
    data: IEnrichedFeedItemData;
    type: 'client_enrichment';
    contact?: {
        full_name: string;
        profile_image?: {
            url?: string;
        };
    };
}

export interface IFeedItem {
    activity: any;
    cachable: boolean;
    comments: [];
    created_at: string;
    flow?: Flow;
    data: IFeedItemData;
    feed_tracking: [];
    relevant_to_user_ids?: string[];
    email_participant_ids?: string[];
    to_users_full_names?: string[];
    from_workflow_title?: string;
    item_origin: 'honeybook' | 'gmail' | 'nylas' | 'postmark';
    email_tracking?: IReminderTracking[];
    is_commercial?: boolean;
    hidden: boolean;
    origin_date: string;
    origin_id: string;
    scheduled_for: string;
    seen_at: string;
    sender_id: string;
    type: string;
    type_cd: number;
    updated_at: string;
    _id: string;
    _lastServerUpdate: string;
    is_ghost?: boolean; // fake feed item
    cta_button_text?: string;
}

export interface IWorkspaceFeed {
    feed_items: IFeedItem[];
}

export interface IFeed {
    _id: string;
    feed_items: IFeedItem[];
    feed_users?: Record<string, Partial<IUserResponse>>;
    has_pvl_request?: boolean;
    show_system_generated_items?: boolean;
}

export interface IEmailSender {
    _id: string;
    address?: string;
    audited_companies?: string[];
    bookkeeperModeCompanyId?: string;
    client_id?: string;
    company?: Partial<ICompanyResponse>;
    company_role?: string;
    contact_id?: string;
    custom_user_fields?: string[];
    email_signatures?: IEmailSignature[];
    email?: string;
    first_name?: string;
    full_name?: string;
    hb_user_type?: HbUserType;
    initials?: string;
    isMemberOfWorkspace?: boolean;
    is_company_owner?: boolean;
    job_title?: string;
    last_name?: string;
    notes?: string[];
    organization?: IOrganization;
    phone_number?: string;
    profile_image?: IImage;
    profile_image_url?: string;
    subtype_consultants_adjustments_feature_date?: string;
    system_user_type?: SystemUserType;
    user_type?: UserType;
    user_type_name?: string;
    workspaceId?: string;
    workspace_member_type?: TWorkspaceMemberType;
    workspace_user_data?: IWorkspaceUserDataResponse;
}

export interface ILegacyFilePreview {
    cover_image_cloudinary_public_id: string;
    file_title: string;
    _id: string;
}

export interface IContactFeedItemWorkspace {
    event: {
        event_name?: string; // can be optional in case of contact_workspace
        _id?: string; // can be optional in case of contact_workspace
    };
    is_archived: boolean;
    company_id: string;
    _id: string;
}

export interface IContactFeedItem extends IFeedItem {
    workspace: IContactFeedItemWorkspace;
    sender?: IEmailSender;
}

export type TAIMeetingNoteType =
    | 'ai_meeting_note_generated'
    | 'ai_meeting_recap_generated'
    | 'ai_project_recap_generated'
    | 'ai_lead_suggestion';

export type IAISummaryNote = {
    type: TAIMeetingNoteType;
    meeting_note_id: string;
    feed_creation_date: string;
    workspace_id: string;
    calendar_item_id: string;
};

export interface TAIMeetingNote extends IAISummaryNote {
    meeting_start_date: string;
    meeting_end_date: string;
    meeting_name: string;
}

export interface TAIProjectRecapsNote extends IAISummaryNote {
    project_name: string;
    project_date: string;
}

export interface TAILeadSuggestionNote extends IAISummaryNote {
    client_full_name: string;
}

export interface IAIMeetingNoteFeedItem extends IFeedItem {
    meetingNote: TAIMeetingNote | TAIProjectRecapsNote;
}

export type TGenericAINote =
    | IAISummaryNote
    | TAIMeetingNote
    | TAIProjectRecapsNote
    | TAILeadSuggestionNote;

export type EmailStatus = 'failed' | 'sent' | 'viewed' | 'bounced';

export interface IEmailTracking {
    tracked_obj_id: string;
    recipient_id: string;
    recipient_name: string;
    recipient_type: 'Client' | 'Creative';
    status: EmailStatus;
    date?: string;
    error?: string;
}

export type SmsStatus =
    | 'unsupported'
    | 'scheduled'
    | 'sending'
    | 'sent'
    | 'delivered'
    | 'failed';

export type SmsUnsupportedError = 'not_supported' | 'unsupported_legacy_client';

export type SmsError =
    | 'account_not_eligible'
    | 'account_not_in_experiment'
    | 'client_phone_number_differs'
    | 'destination_unreachable'
    | 'general_error'
    | 'invalid_phone_number'
    | 'no_consent'
    | 'no_phone_number'
    | 'non_prod_not_test_phone_number'
    | 'sms_config_off'
    | 'twilio_error'
    | 'unsubscribed'
    | SmsUnsupportedError;

export interface IReminderTracking extends IEmailTracking {
    sms_status: SmsStatus;
    sms_error?: SmsError;
    sms_delivered_at?: string;
    sms_scheduled_for?: string;
}

export interface IFeedItemFlowActions {
    updateAfterDelete: (feedItemId: string) => void;
    udpateAfterRename: (title: string) => void;
    updateAfterDeleteFlow: (flowid: string) => void;
}

export interface IAutomationEmailSettings {
    is_logo_enabled: boolean;
    cta_button_text: string;
}

export type ICompanyFont = {
    name: string;
    url?: string;
    is_deleted?: boolean;
};

// the commented out fields are always blank, but they are a part of the DB model :shrug:
export interface IAsset {
    _id: string;
    asset_name: string;
    asset_type: 'file' | 'image' | 'bookmark';
    cloudinary_public_id: string | null;
    cloudinary_sub_account: string;
    cloudinary_url?: string | null;
    company_id?: string | null;
    created_at: string;
    creator_id: string;
    deleted_at: string | null;
    file_type: string | null;
    flow_id?: string;
    img_height?: number | null;
    img_width?: number | null;
    original_file_name: string | null;
    s3_bucket: string | null;
    s3_key: string | null;
    s3_url: any | null;
    size?: number | null;
    source_type: string;
    updated_at: string;
    url: string;
    user_class: string;
    workspace_id: string;
    dataUrl?: string;
}

export interface IFileAsset extends IAsset {
    asset_type: 'file';
    isUpload?: boolean;
}

export interface IImageAsset extends IAsset {
    asset_type: 'image';
    isUpload?: boolean;
}

export interface ICoverImage {
    _id: string;
    url: string;
    original_url: string;
    position_left: any;
    position_top: any;
    position_x: number;
    position_y: number;
    position_w: number;
    position_h: number;
    crop_ratio: any;
    resize_ratio: any;
    crop_type: any;
    _type: 'CoverImage';
    cloudinary_public_id: string;
    width: number;
    height: number;
    cloudinary_sub_account: string;
    phash: string;
    name: string;
    file_type: string;
}

export type IEmailAttachment = {
    _id: string;
    asset_name?: string;
    asset_type: 'file' | 'image';
    created_at?: string;
    file_mime_type?: string;
    file_name: string;
    file_url?: string;
    s3url?: string;
    url?: string;
    upload_progress?: number;
    preview_data_url?: string; // for images, before they are uploaded
    name?: string;
};

export const TCustomProjectField = z.object({
    _id: z.string(),
    name: z.string(),
    identifier: z.string()
});
export type ICustomProjectField = z.infer<typeof TCustomProjectField>;

export interface IContract {
    html_body: string;
    fields_metadata: ISmartField[];
}

export type SignatureType = 'typed' | 'drawn';
export type SigneeType = Extract<SystemUserType, 'vendor' | 'client'>;

export interface ISignature {
    signee_id: string;
    signee_name: string;
    signee_email: string;
    signee_type: SigneeType;
    required?: boolean;
    signee_details: string;
}

export const CONTACT_FILTERS = [
    'has_project',
    'no_project',
    'has_company',
    'has_organization',
    'is_preferred',
    'gift_card_lead',
    'gift_card_purchased',
    'gift_card_redeemed',
    'is_unsubscribed'
] as const;

export type TContactsListFilter = (typeof CONTACT_FILTERS)[number];

export type TFilterTypes =
    | TFlowActionType
    | TBlockComponentsFilterByBlockTypes
    | TContactsListFilter
    | THBExecutionStatus
    | ExecutionStatus;

export type TInvoiceType = 'payment_schedule' | 'recurring';
export type TInvoiceSectionType = 'tax' | 'payments';
export type TTimeUnitType = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type TRepetitionUnitType = TTimeUnitType | 'custom';
export type TCustomTimeUnitType = 'days' | 'weeks' | 'months' | 'years';
export type TEndType = 'never' | 'fixed_date' | 'repetition_count';

export const CUSTOM_TIME_UNIT_TO_REPETITION_UNIT: Record<
    TCustomTimeUnitType,
    TTimeUnitType
> = {
    days: 'daily',
    weeks: 'weekly',
    months: 'monthly',
    years: 'yearly'
};

export interface IRecurringSchedule {
    repetition_unit_type: TRepetitionUnitType;
    custom_unit_count: number;
    custom_unit_type: TCustomTimeUnitType;
    month_of_year: TMonths;
    day_of_month: number;
    day_of_week: TDays;
    first_invoice_due_date: string;
    end_type: TEndType;
    repetition_count: number;
    end_date: string;
}

export type TSmartFieldType = 'field' | 'payments_recap' | 'services_recap';

export type ISmartField = {
    field_id: string;
    connected_variable_id?: string;
    name: string;
    type: TSmartFieldType;
    placeholder: string;
    // TODO: change all references to use enum
    fill_type: SmartFieldPermissions | `${SmartFieldPermissions}`;
    default_value: string;
    data_source_type?: string;
    data_source_property: string;
    data_source_sub_property?: string;
    source_field_id?: string;
    source_variable_id?: string;
};

export type TFileNameFormatOption =
    | 'template_name'
    | 'file_type'
    | 'project_name'
    | 'project_with_template_name'
    | 'project_with_file_type'
    | 'project_with_template_name_or_file_type'
    | 'project_with_flow_or_file_type';

export type TProjectNameType =
    | 'client_name_project_type'
    | 'client_name_project'
    | 'client_name_event'
    | 'client_name'
    | 'client_name_inquiry_name';

export interface IAccountSettings {
    file_from_template_name: TFileNameFormatOption;
    file_from_workflow_name: TFileNameFormatOption;
    default_project_name_type: TProjectNameType;
    // _id: string;
}

export interface IPricingPlanCancellation {
    canceled_by: string;
    canceled_on: string;
    reason: string;
    reasons: string[];
}

export interface IPricingPlan {
    active: boolean;
    amount: number;
    canceled: boolean | IPricingPlanCancellation;
    created_at: string;
    in_app_purchase_device: string;
    in_app_purchase_product: string;
    monthly_payment_period: number;
    next_charge_date: string;
    next_payment_amount: number;
    original_amount: number;
    payment_method: IBillingInfo;
    plan_end_date: string;
    plan_name: string;
    plan_start_date: string;
    plan_type: TPlanCode;
    promo_code_data?: IPromoCodeData;
    tier: string;
    is_active_dunning?: boolean;
    dunning_retry_count?: number;
    _id: string;
}

interface IFee {
    fee_percent: number;
    fee_fixed_in_cents: number;
}

export interface ITransactionFees {
    ach: IFee;
    card_on_file: IFee;
    cardholder_entered: IFee;
    installment: IFee;
    recurring: IFee;
    virtual_terminal: IFee;
}

export interface IPromoCodeData {
    bank_transfer_fee: number;
    code: string;
    credit_card_fee: number;
    discounted_amount: number;
    discounted_payments: number;
    free_payments: number;
    pricing_plan_id: string;
    promo_code_id: string;
    _id: string;
}

export interface IBillingInfo {
    first_name: string;
    last_name: string;
    card_type: string;
    last_four: string;
}

export interface ICoupon {
    code: string;
    coupon_code: string;
    coupon_discount_amount?: number;
    coupon_discount_percent?: number;
    coupon_discount_type: string;
    coupon_duration: string;
    coupon_name: string;
    coupon_temporal_amount?: number;
    coupon_temporal_unit?: string;
    discounted?: number;
    expires_at?: string;
    redemption_id?: string;
    removed_at?: null;
    state?: string;
    _id: string;
}

export interface ISubscription {
    _id: string;
    amount: number;
    total_now: number;
    total_next: number;
    discounted_now: number;
    discounted_next: number;
    next_charge_date: string;
    payment_method: IBillingInfo;
    matches_plan: boolean;
    state: string;
    plan_name: string;
    plan_code: TPlanCode;
    current_period_start_date: string;
    valid?: boolean;
    active?: boolean;
    paused?: boolean;
    pause_end_date: string;
    paused_start_date: string;
    pause_remaining_cycles: number;
    plan_monthly_payment_period?: number;
    pricing_plan: IPricingPlan;
    coupon?: ICoupon;
    start_date?: string;
}

export interface ITrial {
    days_to_end: number;
    duration: number;
    ended: boolean;
    extended_times: number;
    max_payments: number;
    n_to_end: number;
    payments_received: number;
    start_date: string;
    type: string;
}

export interface IOwner extends ITeamMember {}

export type QuickbooksAccountKeys =
    | 'deposit_account_id'
    | 'tax_code'
    | 'zero_tax_code'
    | 'refund_account_id';

interface QuickbooksSetting {
    selected: Option;
    options: Option[];
}

export interface QuickbooksPref {
    id: string;
    status: boolean;
    name: string;
}

export interface QuickbooksSettings {
    incoming_deposits: QuickbooksSetting;
    service_charge: QuickbooksSetting;
    tax_rate: QuickbooksSetting;
    zero_rated_tax_rate: QuickbooksSetting;
    refunds: QuickbooksSetting;
    preferences: {
        discount_prefs: QuickbooksPref;
        gratuity_prefs: QuickbooksPref;
    };
}

export interface QuickbooksItem {
    id: string;
    name: string;
    type: string;
}

export interface IQuickbooksIntegration {
    _id: string;
    connected_at: string;
    items?: QuickbooksItem[];
}

export interface ITeamMember {
    _id: string;
    full_name: string;
    initials: string;
    email: string;
    system_user_type: SystemUserType;
    profile_image?: IImage;
    is_disabled?: boolean;

    dataOnly?(): ITeamMember;

    is_pro?: boolean;
    address?: string;
    job_title?: string;
    phone_number?: string;
    onboarding_container?: Partial<IOnboardingContainer>;
    attribution?: IAttribution;
    profile_image_url?: string;
}

export interface IAuditeeCompany
    extends Pick<
        ICompanyResponse,
        | '_id'
        | 'company_name'
        | 'quickbooks_integration'
        | 'icon_image'
        | 'logo_image'
        | 'brand_color'
    > {
    account_id: string;
    account: {
        feature_plan: { feature_variations: TFeatureVariation[] };
    };
}

export type TAvailabilityType = 'busy' | 'free';
export type IEvent = {
    _id: string; // workspace_id
    created_at?: string;
    custom_project_fields: ICustomProjectField[];
    custom_project_fields_v2?: ICustomFieldValue[];
    is_in_view_mode?: boolean;
    event_name: string;
    event_type: string;
    event_date: string | null;
    event_end_date?: string | null;
    event_time_start: string | null;
    event_time_end: string | null;
    event_location: string;
    event_timezone: TShortTimezone;
    event_budget?: number;
    event_guests?: number;
    event_details?: string;
    hide_conflict_pvl_action?: boolean;
    event_image?: IImage;
    spaces?: IEventSpaces[];
    availability_type?: TAvailabilityType;
    workspace_id?: string;
    project_dates?: IProjectDate[];
    lead_source?: string;
    lead_source_open_text?: string;
    workspaces?: IWorkspace[];
    project_type?: string;
    pictime_projects?: IPicTimeConnectedGallery[];
    is_client_portal_exposed?: boolean;
    created_from_lead_form_id?: string;
    created_from_contact_form_id?: string;
};

export type IMinimalEvent = Pick<
    IEvent,
    '_id' | 'event_name' | 'event_date' | 'event_type' | 'workspace_id'
>;

export interface IEventSpaces {
    event_id: string;
    space_id: string;
}

export interface IProjectDate {
    _id: string;
    date_label: string;
    event_date: string | null;
    event_end_date: string | null;
    event_time_end: string;
    event_time_start: string;
    location: string;
    location_label: string;
}

export interface IWorkspaceFile {
    _id: string;
    owner: {
        _id: string;
    };
    isUpload?: boolean;
}

export interface IBookmark extends IAsset {
    _id: string;
    asset_type: 'bookmark';
    favicon: string;
    url: string;
    asset_name: string;
    default_permission: TPermissionType;
    permissions: IPermission[];
}

export interface IUploadQuestionAsset extends IAsset {
    asset_type: 'file' | 'image';
    cloudinary_url: string | null;
    company_id: string | null;
    deleted_at: string | null;
    flow_id: string;
    group_id: string | null;
    network_project_id: string | null;
}

export type TPermissionType = 'no_permissions' | 'view_only' | 'full_access';

export interface IPermission {
    permission: TPermissionType;
    user_id: string;
}

//TODO find actual type
export interface IWorkspaceAttachments {
    images?: IImageAsset[];
    files?: IFileAsset[];
    bookmarks?: IBookmark[];
}

export interface IWorkspace extends Omit<IWorkspaceResponse, 'members'> {
    attachments?: IWorkspaceAttachments;
    deleted_members: IMember[];
    members: IMember[];
    company_id: string;
}

export interface IEventWithWorkspaces extends IEvent {
    workspaces: IWorkspace[];
}

export interface IS3File {
    url: string;
    data: string;
}

export type TCropRatio =
    | 'Original'
    | 'Custom'
    | '4:3'
    | '3:4'
    | '16:9'
    | '9:16'
    | '1:1'
    | 'Circle';

export interface IImage {
    internalId?: string;
    _id?: string;
    url?: string;
    cloudinary_url?: string;
    original_url?: string;
    position_left?: number;
    position_top?: number;
    position_x?: number;
    position_y?: number;
    position_w?: number;
    position_h?: number;
    _type?: string;
    cloudinary_public_id: string;
    width?: number;
    height?: number;
    cloudinary_sub_account?: string;
    phash?: string | null;
    filename?: string;
    filesize?: number;
    filetype?: string;
    crop_ratio?: TCropRatio | string;
    crop_type?: 'pad';
    resize_ratio?: number;
    is_default_company_icon?: boolean;
    name?: string;
    isUpload?: boolean;
    created_at?: string;
}

export interface IFlowThumbnail extends IImage {}

export interface IRatio {
    title: TCropRatio;
    value?: number;
    iconStyle?: { width: string; height: string; borderRadius?: string };
}

export type TFlowStatus =
    | 'draft'
    | 'viewed'
    | 'sent'
    | 'in_progress'
    | 'completed';

export enum FlowStatus {
    Draft = 'draft',
    Viewed = 'viewed',
    Sent = 'sent',
    InProgress = 'in_progress',
    Completed = 'completed',
    Expired = 'expired'
}

export enum LeadFormStatus {
    Draft = 'draft',
    Live = 'live',
    Inactive = 'inactive'
}

export enum TaxType {
    Relative = 'relative',
    Absolute = 'absolute'
}

export enum DiscountTypes {
    Relative = 'relative',
    Absolute = 'absolute'
}

export enum ServiceItemUnit {
    Item = 'item',
    Hour = 'hour',
    Day = 'day',
    Week = 'week',
    Month = 'month',
    None = ''
}

export type TMonths =
    | 'January'
    | 'February'
    | 'March'
    | 'April'
    | 'May'
    | 'June'
    | 'July'
    | 'August'
    | 'September'
    | 'October'
    | 'November'
    | 'December';

export type TDays =
    | 'Sunday'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday';

export enum DateCalcType {
    DateSent = 'date_sent',
    Fixed = 'fixed',
    SmartDate = 'smart_date',
    MidWay = 'midway',
    ProjectDate = 'project_date',
    Milestone = 'milestone',
    Today = 'today'
}

export enum AmountCalcType {
    Fixed = 'fixed',
    Percentage = 'percentage',
    DivideEqually = 'divide_equally'
}

export enum PaymentStatus {
    Paid = 'PAID',
    Pending = 'PROCESSING',
    Overdue = 'OVERDUE',
    Upcoming = 'UPCOMING',
    Refunded = 'FULL REFUND',
    Refunding = 'REFUND IN PROCESS',
    Canceled = 'CANCELED',
    Unpaid = 'DUE TODAY',
    DueToday = 'DUE TODAY',
    PartiallyRefunded = 'PARTIAL REFUND',
    PendingRefund = 'PENDING REFUND',
    DisputePending = 'DISPUTE IN PROCESS',
    DisputeReserved = 'HELD FOR DISPUTE',
    DisputeReservedLost = 'PAID*',
    DisputeWon = 'DISPUTE WON',
    DisputeLost = 'DISPUTE LOST',
    DisputePartiallyWon = 'DISPUTE PARTIALLY WON',
    DisputeWonRefunded = 'DISPUTE WON/ FULL REFUND',
    DisputeWonPartiallyRefunded = 'DISPUTE WON/ PARTIAL REFUND'
}

export enum SmartDatePeriodType {
    Day = 'day',
    Week = 'week',
    Month = 'month'
}

export enum SmartDatePeriodRelation {
    BeforeProject = 'before_project',
    AfterProject = 'after_project',
    AfterSent = 'after_sent'
}

export enum SignatureTypes {
    TYPE = 'typed',
    DRAW = 'drawn'
}

export enum TInvoiceTypes {
    PaymentSchedule = 'payment_schedule',
    Recurring = 'recurring'
}

export type TGratuityTypes = 'all_payments' | 'last_payment';

export interface IServiceItem {
    title: string;
    price_per_unit: number;
    quantity: number;
    unit: string | null;
    description?: string;
    image?: IImage;
    service_asset?: string;
    qb_item_id?: string;
}

export interface INewServiceTemplate extends IServiceItem {
    sub_items?: IServiceItem[];
    source: ServiceTemplateSource;
}

export interface IServiceTemplate extends INewServiceTemplate {
    _id: string;
    company_id: string;
    is_original_ai_template: boolean;
}

export interface ISortServices {
    key: TSortFunctionKeys;
    direction: TSort;
}

export interface IService extends IServiceItem {
    price: number;
    tax_1: boolean;
    tax_2: boolean;
    tax_3: boolean;
    service_template_id?: string;
    is_linked?: boolean;
    qb_item_id?: string;
}

export enum ServiceTemplateSource {
    Hub = 'hub',
    OnBoarding = 'on_boarding',
    SetUpGuide = 'setup_guide',
    TopNavNew = 'top_nav_create_new',
    AI = 'ai',
    Builder = 'builder',
    InvoiceWizard = 'invoice_wizard'
}

export enum ServiceItemType {
    Service = 'service',
    Package = 'package'
}

export enum ServicesItemsLayoutType {
    Rows = 'rows',
    TwoColumns = 'two_columns',
    ThreeColumns = 'three_columns'
}

export enum ServiceSelectionType {
    One = 'one',
    Multiple = 'multiple',
    NoSelection = 'no_selection'
}

export interface IServiceSelectionItemsStyles {
    title: IBlockFakeCkEditorStyles;
    service_pricing: IBlockFakeCkEditorStyles;
    service_pricing_link: IBlockFakeCkEditorStyles;
    total_price: IBlockFakeCkEditorStyles;
    service_select_button: IBlockFakeCkEditorStyles;
    sub_item_title: IBlockFakeCkEditorStyles;
    service_sub_item_pricing: IBlockFakeCkEditorStyles;
    sub_item_total_price: IBlockFakeCkEditorStyles;
}

export type TServiceSelectionItemsStylesKeys =
    keyof IServiceSelectionItemsStyles;

export interface IInvoiceSummary {
    discount: number;
    discount_type: DiscountTypes;
    tax_1?: ITaxResponse;
    tax_2?: ITaxResponse;
    tax_3?: ITaxResponse;
    compound_tax_id?: string;
    late_fee_percentage: number;
    late_fee_enabled: boolean;
    is_client_payment_plan?: boolean;
    max_number_of_payments?: number;
    first_payment_due_date_type?: DateCalcType;
    first_payment_due_date?: string;
    allow_pay_in_full_discount?: boolean;
    pay_in_full_discount?: number;
    pay_in_full_discount_type?: DiscountTypes;
}

export interface IInvoiceHeader {
    title: string;
    bill_from_content?: IBillFromContent;
    project_details?: IInvoiceProjectDetails;
}

export interface IRecurringPayment {
    _id: string;
    flow_id: string;
    amount: number;
    due_date: string;
    charge_date: string;
    invoice_id: string;
    is_paid: boolean;
    is_refunded: boolean;
    is_pending: boolean;
    payment_id: string;
    invoice: string;
    invoice_number: string;
    payment_block_id?: string;
}

export interface IMinimalPaymentContent {
    date_calc_type?: DateCalcType;
    smart_date_period?: number;
    smart_date_period_type?: SmartDatePeriodType;
    smart_date_period_relation?: SmartDatePeriodRelation;
    amount_calc_type?: AmountCalcType;
    percentage?: number;
    fixed_amount?: number;
    milestone_title?: string;
}

export interface IPaymentContent extends IMinimalPaymentContent {
    fixed_date?: string;
}

export interface IAttribution {
    id: string;
    created_at: string;
    creator_type: string;
    first_login_device_type: string;
    first_login_ip: string;
    first_login_location: ILoginLocation;
    first_login_sso_type: string;
    first_login_t: string;
    first_login_user_agent: string;
    original_referer: string;
    pre_first_login_actions: Array<any>;
    signup_landing_page: string;
    updated_at: string;
    user_general_tags: IUserGeneralTags;
    user_id: string;
    user_source_3rd_party: string;
    user_source_network: string;
    user_source_other: string;
    user_source_primary: string;
    user_source_product: string;
    user_source_sales: string;
    user_source_secondary: string;
    user_source_signup: string;
    user_source_widget: string;
    user_utm_params: IUtmParams;
    version: number;
    external_id: string;
    template_parameters: {
        created_at: string;
        imported_template_id: string;
        member_referral_id: string;
        source_template_id: string;
        template_downloaded_date: string;
        template_sharing_platform: string;
        type: string;
        updated_at: string;
        _id: string;
    };
}

export interface ILoginLocation {
    area_code: number;
    city: string;
    country_code: string;
    country_name: string;
    created_at: string;
    ip: string;
    latitude: number;
    longitude: number;
    metro_code: number;
    postal_code: string;
    region: string;
    source: string;
    updated_at: string;
    _id: string;
}

export interface IUserGeneralTags extends TUserTags {
    how_heard_of_us: string;
    injected_ab_test: string;
    injected_ab_test_variant: string;
    preselected_pricing_plan: string;
    sms_opt_in: boolean;
    first_flow_id: string;
}

export interface IUtmParams {
    utm_mbsy: string;
    utm_referral_source: string;
    utm_source?: string;
    utm_campaign?: string;
    promo_code?: string;
    utm_medium?: string;
    utm_customer?: string;
    utm_initiative?: string;
    partnership?: TPartner;
}

export interface IResponsesData {
    actionBlocks?: TResponseBlock[];
    pages?: IResponsesPage[];
    variables?: Variable<unknown>[];
    pagesViewed?: number;
    questionsCompleted?: number;
    questionsTotal?: number;
    requiredQuestionsCompleted?: number;
    requiredQuestionsTotal?: number;
    requiredAttachmentsTotal?: number;
    isPublished?: boolean;
    lastViewedAt?: string;
    firstPublishedAt?: string;
    publishedAt?: string;
    status?: TFlowStatus;
    submittedOn?: string;
    submittedBy?: { first_name?: string; last_name?: string };
    canceledOn?: string;
}

export interface IResponsesFilteredPages<T> {
    [index: string]: T;
}

export interface IResonsesFilteredBlocks<T> {
    [index: string]: T;
}

export interface IResponsesPage extends Partial<Page> {
    responseBlocks?: IResponseBlock[];
    displayOrder?: number;
}

export interface IResponseBlock {
    type: TResponsesBlockTypes;
    content: TResponseBlock | QuestionBlock[];
}

export interface IResponsesClient extends IMember {
    numberOfViewedPages: number;
    lastViewedPage: Page;
    hasClientCompletedFlow: boolean;
    profileImageUrl: string;
}

export interface ICanceled {
    canceled_on: string;
}

export enum RevisionType {
    Draft = 'draft',
    Active = 'active'
}

export interface IFolder {
    company_id: string;
    order: number;
    path: string;
    _id: string;
    created_at?: string;
    updated_at?: string;
}

export interface IVendorPackage {
    base_services_count: number;
    title: string;
    total_price: number;
    _id: string;
    created_at: string;
}

export interface ITemplateBase {
    created_at?: string | Date;
    title?: string;
    _id?: string;
    type?: TTemplateType;
    viewModelKey?: TTemplateViewModelKey;
}

export interface IInvoice extends ITemplateBase {}

export interface IAgreement extends ITemplateBase {
    name: string;
}

export interface IProposal extends ITemplateBase {
    name: string;
}

export interface IContactForm extends ITemplateBase {
    contact_form_flow_type: 'default' | 'gift_card';
    data_model: string;
    last_publish_timestamp: string;
    website_views_count: number;
    contact_form_settings?: {
        is_active?: boolean;
    };
    isPublished?: () => boolean;
    are_changes_published?: boolean;
}

export interface IBrochure extends ITemplateBase {
    isOldBrochure?: boolean;
}

export interface IBookingReview {
    _id: string;
    client: IUserResponse;
    company: Company;
    project: Project;
    feedback_date: string;
    rating_score: number;
    rating_comment?: string;
}

export enum FlowActionSummaryType {
    Contract = 'Contract',
    Service = 'Service',
    Invoice = 'Invoice',
    Scheduler = 'Scheduler',
    RecurringInvoice = 'Recurring Invoice'
}

export enum FlowActionSummaryStatus {
    Pending = 'pending',
    InProgress = 'in_progress',
    Completed = 'completed'
}

export interface IFlowActionSummary {
    block_id: string;
    type: FlowActionSummaryType;
    page_number: number;
    status: FlowActionSummaryStatus;
    contract?: IContractActionSummary;
    service?: IServiceActionSummary;
    invoice?: IInvoiceActionSummary;
    scheduler?: ISchedulerActionSummary;
    is_required: boolean;
}

interface IContractActionSummary {
    signatures_signed: number;
    total_signatures: number;
    signed_required_signatures: number;
    total_required_signatures: number;
}

interface IServiceActionSummary {
    is_chargeable: boolean;
    selection_made: boolean;
    viewed_on: string;
    total_selected_services: number;
    total_services: number;
}

interface IInvoiceActionSummary {
    payments_paid: number;
    total_payments: number;
}

interface ISchedulerActionSummary {
    is_scheduled: boolean;
}

export interface ILibrarySelection {
    files: IEmailAttachment[];
    images: IEmailAttachment[];
}

export interface IABTests {
    gallery_push_to_pros: 'control' | 'copy_a' | 'copy_b';
    smart_files_builder_onboarding: 'control' | 'video';
    simplified_create_template: 'control' | 'simplified_create_template';
    redirect_destination_after_subscription:
        | 'control'
        | 'b_redirect_destination';
    intent_infra: 'holdout' | 'intent_test';
    growth_2024: 'holdout' | 'test';
    builder_tour_invoice: 'intent_control' | 'intent_builder';
    tal_bucket_test: 'tb_control' | 'tb_push_to_activation';
    scheduler_intent_onboarding: 'control' | 'scheduler_onboarding';
    invoice_intent_services_onboarding:
        | 'control_legacy_like'
        | 'manual_services';
    home_payments_poc:
        | 'control_trialer_home'
        | 'variant_easy'
        | 'variant_client'
        | 'variant_business'
        | 'variant_shuffle';
    contract_tour: 'control' | 'variant_contract_tour';
    contract_intent_onboarding_v3:
        | 'control_original_templates'
        | 'v_contract_templates';
    t1_activation_abc: 'control' | 'legacy_like' | 'skip_template'; // Done: 'control' + 'legacy_like'
    contract_intent_onboarding_v2:
        | 'control_original_templates'
        | 'v_contract_templates'; //Done: no winner
}

export type TABTestKeys = keyof IABTests;
export type TABTestValue<T extends TABTestKeys> = IABTests[T];

export interface IImageDimension {
    height: string;
    width: string;
}

export enum TemplateSourceType {
    ReferralTrialTemplates = 'referral_trial_templates',
    MalkutImport = 'malkut_import',
    ImportGallery = 'import_gallery',
    PreUser = 'pre_user',
    ImportPublic = 'import_public',
    Manual = 'manual',
    DuplicateTemplate = 'duplicate_template',
    FlowMigration = 'flow_migration',
    RecentInstances = 'recent_instances',
    MyTemplates = 'my_templates',
    SaveNewTemplate = 'save_new_template',
    BlockTemplate = 'block_template',
    TimeTracking = 'time_tracking',
    ServiceSelectionInvoice = 'service_selection_invoice',
    PublicFlow = 'public_flows'
}

export enum FlowSourceType {
    ImportPdf = 'import_pdf',
    AiTemplates = 'ai_templates'
}

export enum UserIntent {
    Invoice = 'invoices',
    Contract = 'contracts',
    Automations = 'automations',
    LeadCapture = 'leadCapture',
    BusinessManagement = 'businessManagement',
    CRM = 'crm',
    Scheduling = 'scheduling'
}

export interface ISessionSlotsPerMonthOfTheYear {
    monthOfTheYear: string;
    slots: TSessionTimeSlot[];
}

export interface ITooltipActionItem {
    description: string;
    completion_status: string;
}

export interface IActionTooltipData {
    [K: number]: ITooltipActionItem[];
}

export interface IActionsCheckListItem {
    index: number;
    isInProgressOrCompleted: boolean;
    text: string;
    textColor: keyof Pick<typeof colors, 'neutral700' | 'neutral900'>;
    isCompleted: boolean;
    isLastAction: boolean;
    tooltipData: IActionTooltipData;
}

export type TFlowContext = 'flowTemplate' | 'flowInstance' | 'publicFlow';

export type TSmsType =
    | 'sms_enabled'
    | 'client_2fa_sms_enabled'
    | 'sms_payment_reminders_enabled'
    | 'sms_meeting_reminders_enabled';

/**
 * @deprecated - gate and experiment code is throwaway so no need to keep names in a neverending enum
 * Use a hardcoded string instead (will make the throwaway code easier to implement and to remove)
 * If the gate or experiment name is used in multiple places, you can store it in a const
 */
export enum StatsigExperiments {
    invoiceAwarenessCopyTest = 'invoice_awareness_copy_test', //['control_original', 'variant_firetask']
    stableIdAATest = 'stableid_aa_test_experiment', //['control', 'variant']
    paymentSurveyV2 = 'payment_survey_v2',
    trialDurationTest = 'trial_length_2023_test', // ['control_7d', 'variant_30d']
    consultantsAdjustments = 'consultants_adjustments', // [true, false]
    companySubTypeSelection = 'company_sub_type_selection',
    instantBranding = 'brand_import',
    newIntegrationsPage = 'new_integrations_page',
    publicFilesSetupGuide = 'public_files_setup_guide',
    shareSendSmartFile = 'share_save_smart_file_exp',
    previewFlowAfterOnboarding = 'preview_flow_after_onboarding',
    servicesHubPage = 'services_hub_page', // 'show_page': true / false
    servicesHubPageForConsultants = 'services_hub_page_consultants', // 'show_page': true / false
    leadFormsDigitalProducts = 'lead_forms_digital_products', // digital_products_enabled: true / false
    onboardingQuiz2023InvoiceIntent = 'onboarding_quiz_2023_invoice_intent', // ['c_none', 'v_invoice_onboarding']
    leadFormsNewCxNav = 'lead_forms_new_cx_nav', // enabled: true / false
    setupGuideForNevs = 'setup_guide_for_nevs',
    setupGuideForContractNevs = 'setup_guide_for_contract_nevs'
}

/**
 * @deprecated - gate and experiment code is throwaway so no need to keep names in a neverending enum
 * Use a hardcoded string instead (will make the throwaway code easier to implement and to remove)
 * If the gate or experiment name is used in multiple places, you can store it in a const
 */
export enum StatsigFeatureGates {
    clientInfoReport = 'client_info_report',
    contactView = 'contact_view',
    UGAM = 'ng2react_ugam_modal',
    decLoveDynamicGratuity = 'dec_love_dynamic_gratuity',
    organizationViewEnabled = 'organization_view_enabled',
    organizationInWhamEnabled = 'organization_in_wham_enabled',
    organizationViewContactMenuEnabled = 'organization_view_contact_menu_enabled',
    invoiceReport = 'invoice_report',
    createNewClientRedirectTrialers = 'create_new_client_redirect_trialers',
    createNewClientRedirectMembers = 'create_new_client_redirect_members',
    publicFilesSubmissionSettingsEnabled = 'public_files_submission_settings_enabled',
    leadFormsDigitalProductsEvents = 'lead_forms_digital_products_events',
    newPromoOpened = 'new_promo_opened',
    publicFilesAssignTeamMemberSettingsEnabled = 'public_files_assign_team_member_settings_enabled',
    contractInAnalytics = 'contract_in_analytics',
    saveServiceToHub = 'save_service_to_hub',
    globalNewLoveBadgeEnabled = 'plm_global_new_love_badge_enabled',
    tags = 'tags',
    financeOverview = 'finance_app_overview'
}

export type TInputTypeValidation = 'email' | 'number' | 'url';

export type TGratuityVariation = 'control' | 'low' | 'high';

export interface IPaginationData {
    last_page?: boolean;
    offset?: number;
    page?: number;
    total_items?: number;
    total_pages?: number;
}

export type IPaginationResponse<TItemResponse> = {
    items: TItemResponse[];
} & IPaginationData;

export interface IPaginationRequest {
    page?: number;
    perPage?: number;
    reset?: boolean;
}

export interface ISortData {
    key: string;
    direction: TSort;
}

export type TUploadError = 'size_error' | 'type_error';

export type TUploadFailure = 'upload_error';
